/**
 * Heuristic: detect Finnish marketing/prose so we can avoid showing it on EN/SV pages
 * (mixed-language hurts UX and SEO quality signals).
 */

const STRONG_FI_WORD =
  /\b(tarvitsemme|etsimme|haemme|haetaan|liity|tiimiimme|työtehtäviin|työhön|työpaikka|työpaikkaa|suomen\s+kielen|suomea\s+työkielenä|jatkuvasti|käsiä|kokemusta|asiakkaille|palveluissa|toivomme|tarjoamme|palkkaamme|vakituiseen|määräaikaiseen|harjoittelijaksi|sijaisuuteen|tehtäviisi|vastuullasi|yhteistyössä|kehittämään|vahvistamaan)\b/gi;

/** Finnish letters uncommon in typical English employer blurbs of this length */
const FI_ORTHOGRAPHY = /[äöÄÖ]/g;

/**
 * Returns true when `text` is probably Finnish body copy (not just a city or brand name).
 */
export function isLikelyFinnishProse(text: string): boolean {
  const t = text.trim();
  if (t.length < 16) return false;

  let score = 0;
  const strongMatches = t.match(STRONG_FI_WORD);
  if (strongMatches) {
    score += strongMatches.length * 3;
  }

  const orth = t.match(FI_ORTHOGRAPHY);
  if (orth && orth.length >= 2) {
    score += 2;
  } else if (orth && orth.length === 1 && t.length >= 40) {
    score += 1;
  }

  // Short all-caps / codes should not trip this
  if (/^[A-Z0-9\s\-–.,]+$/u.test(t)) {
    return false;
  }

  return score >= 3;
}

/**
 * When the page is not Finnish, hide free-text about blurbs that look like Finnish.
 */
export function filterCompanyAboutForPageLocale(about: string | null | undefined, pageLocale: string): string {
  const trimmed = about?.trim() ?? '';
  if (!trimmed) return '';
  const loc = pageLocale.toLowerCase();
  if (loc === 'fi') return trimmed;
  if (loc === 'en' || loc === 'sv') {
    if (isLikelyFinnishProse(trimmed)) return '';
  }
  return trimmed;
}
